import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { WorkDetailsModel } from "../../../../../../factories/work/model/work-details.model";
import { useAppDispatch } from "../../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import { useGetTemplatesQuery } from "../../../../../../slices/TemplatesSlice";
import { useUpdateWorkTemplateMutation } from "../../../../../../slices/WorkSlice";
import { workTasksSlice } from "../../../../../../slices/WorkTasksSlice";
import { SelectOptionType } from "../../../../../../types/types";

type Props = {
  work: WorkDetailsModel;
  isSubtask?: boolean;
};

const MENU_WIDTH = 200;
const EDGE = 8;

// Subtask template picker in the Subtasks card header (V51/V52 design,
// Roadmap 1848). The menu is rendered in a portal (fixed position) so no
// overflow-hidden parent can cut it off.
export const WorkEditTemplate: FC<Props> = ({ work }) => {
  const { workId } = useParams();
  const dispatch = useAppDispatch();

  const { data: templates, isLoading } = useGetTemplatesQuery({
    orgId: getLinkedFirm()?.orgId || "",
  });
  const [updateTemplate, { data: updatedWork, isSuccess, isError }] =
    useUpdateWorkTemplateMutation();

  const [selectedTemplate, setSelectedTemplate] =
    useState<SelectOptionType | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);

  const templateOptions = useMemo<SelectOptionType[]>(() => {
    const options: SelectOptionType[] = [{ value: "", label: "None" }];

    if (templates) {
      templates?.forEach((item) => {
        options.push({
          value: item?.id,
          label: item?.templateName,
        });
      });
    }

    return options;
  }, [templates]);

  const templateLabel = (): string => {
    if (updatedWork?.templateId === work?.templateId) {
      return work?.template?.templateName || "None";
    }
    if (selectedTemplate) {
      return selectedTemplate.label;
    }
    if (updatedWork) {
      return updatedWork?.template?.templateName || "None";
    }
    if (!selectedTemplate && !work?.templateId) {
      return "None";
    }
    return work?.template?.templateName || "None";
  };

  const template: SelectOptionType = {
    value:
      selectedTemplate?.value ??
      updatedWork?.templateId ??
      work?.templateId ??
      "",
    label: templateLabel(),
  };

  useEffect(() => {
    if (isSuccess) {
      setSelectedTemplate(null);
      dispatch(workTasksSlice.util.invalidateTags(["WorkTask"]));
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (isError) {
      setSelectedTemplate(null);
      toast("Something went wrong", {
        type: "error",
        pauseOnHover: false,
      });
    }
  }, [isError]);

  // Design: right-aligned under the button; if that would leave the screen
  // on the left, align to the button's left edge instead.
  const placeMenu = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    let left = rect.right - MENU_WIDTH;
    if (left < EDGE) {
      left = Math.min(rect.left, window.innerWidth - MENU_WIDTH - EDGE);
    }
    setMenuStyle({ top: rect.bottom + 5, left: Math.max(EDGE, left) });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    placeMenu();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [isOpen, placeMenu]);

  const choose = (option: SelectOptionType) => {
    setIsOpen(false);
    updateTemplate({
      templateId: option?.value,
      workId: workId || "",
    });
    setSelectedTemplate(option);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type={"button"}
        aria-haspopup={"listbox"}
        aria-expanded={isOpen}
        title={template.label}
        onClick={() => setIsOpen((open) => !open)}
        className={
          "inline-flex min-w-0 cursor-pointer items-center gap-[9px] rounded-[8px] border border-[#ECEEF2] bg-[#FAFBFC] px-[11px] py-[6px] font-[inherit]"
        }>
        <span
          className={
            "shrink-0 text-[10px] font-extrabold uppercase leading-none tracking-[0.7px] text-[#9097A8]"
          }>
          Template
        </span>
        <span className={"h-3 w-px shrink-0 bg-[#D6DAE2]"} />
        <span
          className={
            "min-w-[38px] max-w-[160px] truncate text-[13px] font-bold text-[#3A4256]"
          }>
          {template.label}
        </span>
        <svg
          width={11}
          height={11}
          viewBox={"0 0 24 24"}
          fill={"none"}
          stroke={"#9097A8"}
          strokeWidth={2.4}
          strokeLinecap={"round"}
          strokeLinejoin={"round"}
          className={"shrink-0"}
          aria-hidden>
          <path d={"M6 9l6 6 6-6"} />
        </svg>
      </button>

      {isOpen &&
        createPortal(
          <>
            <div
              className={"fixed inset-0 z-[60]"}
              onClick={() => setIsOpen(false)}
            />
            <div
              role={"listbox"}
              aria-label={"Apply a template"}
              style={{ ...menuStyle, width: MENU_WIDTH }}
              className={
                "fixed z-[61] flex max-h-[320px] flex-col overflow-y-auto rounded-[10px] border border-[#ECEEF2] bg-white p-[5px] font-[Inter,sans-serif] shadow-[0_14px_36px_rgba(20,22,34,0.2)]"
              }>
              <div
                className={
                  "shrink-0 px-[11px] pb-[5px] pt-[7px] text-[12.5px] font-bold text-[#9097A8]"
                }>
                {/* Roadmap 1872 (owner): "Apply a template", first letter capital, not all caps. */}
                Apply a template
              </div>
              {isLoading ? (
                <div
                  className={
                    "shrink-0 px-[11px] py-[9px] text-[13.5px] font-semibold text-[#9097A8]"
                  }>
                  Loading…
                </div>
              ) : (
                templateOptions.map((option) => {
                  const selected = option.value === template.value;
                  return (
                    <div
                      key={option.value || "none"}
                      role={"option"}
                      aria-selected={selected}
                      title={option.label}
                      onClick={() => choose(option)}
                      className={`shrink-0 cursor-pointer truncate rounded-[7px] px-[11px] py-[9px] text-[13.5px] font-semibold hover:bg-[#F4F2FF] ${
                        selected
                          ? "bg-[#F4F2FF] text-[#6B4EFF]"
                          : "text-[#1F2433]"
                      }`}>
                      {option.label}
                    </div>
                  );
                })
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
};
